import { num } from '../../utils/invoiceLineMath';

/**
 * What a window.prompt answered: `cancelled` (the person pressed Cancel: do nothing, say nothing),
 * `refused` (an answer that is blank / junk / not above zero: say why, send nothing) or the value.
 * Cancel and blank are different answers; both used to become 0 via Number(...).
 */
export type PromptResult<T> = { kind: 'cancelled' } | { kind: 'refused'; message: string } | { kind: 'ok'; value: T };

type Ask = (question: string, preset?: string) => string | null;

const cancelled = { kind: 'cancelled' } as const;
const refused = (message: string) => ({ kind: 'refused' as const, message });

/** A number above zero. Blank, junk, 0 and negatives are refused: they are never what was meant. */
function askPositive(ask: Ask, question: string, what: string): PromptResult<number> {
  const raw = ask(question);
  if (raw === null) return cancelled;
  const n = num(raw);
  return n !== null && n > 0 ? { kind: 'ok', value: n } : refused(`${what} must be a number above zero.`);
}

function askText(ask: Ask, question: string, what: string): PromptResult<string> {
  const raw = ask(question);
  if (raw === null) return cancelled;
  const text = raw.trim();
  return text ? { kind: 'ok', value: text } : refused(`${what} is required.`);
}

export type WritebackAnswers = { list: 'stock' | 'pantry'; lineNumber: number; actualQuantity?: number; costPerUnitTt?: number };

/** `costOnly`: a sent invoice's quantities are locked (D96.2), so only the TT$ cost is asked for and it is required. */
export function askWriteback(ask: Ask, { costOnly = false }: { costOnly?: boolean } = {}): PromptResult<WritebackAnswers> {
  const list = askText(ask, 'Write back to which list? Type "stock" or "pantry".', 'The list');
  if (list.kind !== 'ok') return list;
  if (list.value !== 'stock' && list.value !== 'pantry') return refused('The list must be "stock" or "pantry".');
  const line = askPositive(ask, 'Line number to update?', 'The line number');
  if (line.kind !== 'ok') return line;
  if (!Number.isInteger(line.value)) return refused('The line number must be a whole number.');
  const qty: PromptResult<number> | null = costOnly ? null : askPositive(ask, 'Actual qty picked?', 'The quantity');
  if (qty && qty.kind !== 'ok') return qty;
  const costRaw = ask(costOnly ? 'TT$ cost per unit? (this invoice is sent - only the cost can change)' : 'TT$ cost per unit? (blank to skip)');
  if (costRaw === null) return cancelled;
  const cost = num(costRaw);
  if (costRaw.trim() !== '' && (cost === null || cost < 0)) return refused('The cost must be a number, zero or more.');
  if (costOnly && cost === null) return refused('Enter the TT$ cost - it is the only thing a sent invoice can take back.');
  return {
    kind: 'ok',
    value: { list: list.value, lineNumber: line.value, ...(qty ? { actualQuantity: qty.value } : {}), ...(cost !== null ? { costPerUnitTt: cost } : {}) },
  };
}

export type ExchangeAnswers = { returnedItemName: string; returnedQty: number; deliveredItemName: string; deliveredQty: number; reason: string };

export function askExchange(ask: Ask): PromptResult<ExchangeAnswers> {
  const returnedItemName = askText(ask, 'Returned item name?', 'The returned item name');
  if (returnedItemName.kind !== 'ok') return returnedItemName;
  const returnedQty = askPositive(ask, 'Returned qty?', 'The returned quantity');
  if (returnedQty.kind !== 'ok') return returnedQty;
  const deliveredItemName = askText(ask, 'Delivered replacement item name?', 'The delivered item name');
  if (deliveredItemName.kind !== 'ok') return deliveredItemName;
  const deliveredQty = askPositive(ask, 'Delivered qty?', 'The delivered quantity');
  if (deliveredQty.kind !== 'ok') return deliveredQty;
  const reason = ask('Reason?');
  if (reason === null) return cancelled;
  return {
    kind: 'ok',
    value: {
      returnedItemName: returnedItemName.value,
      returnedQty: returnedQty.value,
      deliveredItemName: deliveredItemName.value,
      deliveredQty: deliveredQty.value,
      reason: reason.trim(),
    },
  };
}
